import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Snackbar, Alert, Tooltip, IconButton, Chip, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, MenuItem, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PhoneIcon from '@mui/icons-material/Phone';
import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import BuscaPaciente from './BuscaPaciente';

const PRIORIDADE_CONFIG = {
    URGENTE:    { label: 'Urgente',    color: '#c62828', bg: '#ffebee' },
    PRIORITARIO:{ label: 'Prioritário',color: '#e65100', bg: '#fff3e0' },
    NORMAL:     { label: 'Normal',     color: '#2e7d32', bg: '#e8f5e9' },
};

const STATUS_CONFIG = {
    AGUARDANDO: { label: 'Aguardando', color: '#1565c0', bg: '#e3f2fd' },
    CONTATADO:  { label: 'Contatado',  color: '#6a1b9a', bg: '#f3e5f5' },
    AGENDADO:   { label: 'Agendado',   color: '#2e7d32', bg: '#e8f5e9' },
    DESISTIU:   { label: 'Desistiu',   color: '#757575', bg: '#f5f5f5' },
    INATIVO:    { label: 'Inativo',    color: '#9e9e9e', bg: '#fafafa' },
};

const initialForm = {
    idPaciente: '',
    prioridade: 'NORMAL',
    status: 'AGUARDANDO',
    dataEntrada: new Date().toISOString().split('T')[0],
    observacoes: '',
};

export default function FilaEsperaPage() {
    const navigate = useNavigate();
    const [fila, setFila] = useState([]);
    const [pacienteSelecionado, setPacienteSelecionado] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filtroStatus, setFiltroStatus] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        carregar();
    }, []);

    const carregar = async () => {
        setLoading(true);
        try {
            const res = await api.get('/fila-espera');
            const d = res.data;
            setFila(Array.isArray(d) ? d : (d?.content ?? []));
        } catch {
            mostrarMensagem('Erro ao carregar fila.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const filaFiltrada = filtroStatus ? fila.filter(f => f.status === filtroStatus) : fila;

    const handleSalvar = async () => {
        if (!formData.idPaciente || !formData.prioridade) {
            mostrarMensagem('Selecione paciente e prioridade.', 'warning');
            return;
        }
        try {
            await api.post('/fila-espera', formData, { skipGlobalError: true });
            toastSuccess('Paciente adicionado à fila.');
            setOpenDialog(false);
            setFormData(initialForm);
            setPacienteSelecionado(null);
            carregar();
        } catch (error) {
            // Paciente já na fila chega como INTEGRIDADE ou CONFLITO; o catálogo
            // diz qual é o caso em vez de "Erro ao adicionar à fila."
            mostrarErro(error, 'adicionar à fila');
        }
    };

    const handleMudarStatus = async (id, novoStatus) => {
        try {
            await api.patch(`/fila-espera/${id}/status`, null, {
                params: { status: novoStatus },
                skipGlobalError: true,
            });
            toastSuccess('Situação atualizada.');
            carregar();
        } catch (error) {
            mostrarErro(error, 'atualizar a situação');
        }
    };

    const handleAgendar = async (item) => {
        await handleMudarStatus(item.id, 'CONTATADO');
        navigate(`/agendamentos/calendario?pacienteId=${item.idPaciente}&filaId=${item.id}&nomePaciente=${encodeURIComponent(item.nomePaciente)}`);
    };

    const handleDeletar = async (id) => {
        const ok = await confirmAcao({
            title: 'Remover da fila?',
            text: 'O paciente sai da fila de espera.',
            confirmButtonText: 'Remover',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/fila-espera/${id}`, { skipGlobalError: true });
            toastSuccess('Removido da fila.');
            carregar();
        } catch (error) {
            mostrarErro(error, 'remover da fila');
        }
    };

    const mostrarMensagem = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const formatarTelefone = (v) => {
        if (!v) return '—';
        const d = v.replace(/\D/g, '');
        if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
        if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
        return v;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Fila de Espera</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ordenada por prioridade e data de entrada
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenDialog(true)}>
                    Adicionar à Fila
                </Button>
            </Box>

            {/* Filtros de status */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                    label="Todos"
                    onClick={() => setFiltroStatus('')}
                    variant={filtroStatus === '' ? 'filled' : 'outlined'}
                    color={filtroStatus === '' ? 'primary' : 'default'}
                />
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <Chip
                        key={key}
                        label={cfg.label}
                        onClick={() => setFiltroStatus(filtroStatus === key ? '' : key)}
                        sx={{
                            bgcolor: filtroStatus === key ? cfg.color : cfg.bg,
                            color: filtroStatus === key ? '#fff' : cfg.color,
                            border: `1px solid ${cfg.color}`,
                            fontWeight: 600,
                            '&:hover': { bgcolor: cfg.color, color: '#fff' },
                        }}
                    />
                ))}
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                                <TableCell><strong>Paciente</strong></TableCell>
                                <TableCell><strong>Telefone</strong></TableCell>
                                <TableCell><strong>Entrada</strong></TableCell>
                                <TableCell><strong>Prioridade</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>Observações</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filaFiltrada.length > 0 ? filaFiltrada.map((item) => {
                                const priorCfg = PRIORIDADE_CONFIG[item.prioridade] || PRIORIDADE_CONFIG.NORMAL;
                                const statusCfg = STATUS_CONFIG[item.status] || STATUS_CONFIG.AGUARDANDO;
                                return (
                                    <TableRow key={item.id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{item.nomePaciente}</TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PhoneIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                                                {formatarTelefone(item.telefonePaciente)}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            {item.dataEntrada ? new Date(item.dataEntrada + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={priorCfg.label}
                                                sx={{ bgcolor: priorCfg.bg, color: priorCfg.color, border: `1px solid ${priorCfg.color}`, fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell>
                                            <Chip size="small" label={statusCfg.label}
                                                sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, border: `1px solid ${statusCfg.color}`, fontWeight: 600 }} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            <Tooltip title={item.observacoes || ''}>
                                                <span>{item.observacoes || '—'}</span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            {item.status === 'AGUARDANDO' && (
                                                <Tooltip title="Marcar como Contatado">
                                                    <Button size="small" variant="outlined" sx={{ mr: 1, fontSize: '0.75rem' }}
                                                        onClick={() => handleMudarStatus(item.id, 'CONTATADO')}>
                                                        Contatado
                                                    </Button>
                                                </Tooltip>
                                            )}
                                            {['AGUARDANDO', 'CONTATADO'].includes(item.status) && (
                                                <Tooltip title="Agendar sessão">
                                                    <IconButton size="small" color="primary" onClick={() => handleAgendar(item)}>
                                                        <CalendarMonthIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Remover">
                                                <IconButton size="small" color="error" onClick={() => handleDeletar(item.id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            }) : (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">
                                            Nenhum paciente na fila.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Adicionar à Fila de Espera</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <BuscaPaciente
                            label="Paciente *"
                            size="medium"
                            value={pacienteSelecionado}
                            onChange={(v) => {
                                setPacienteSelecionado(v);
                                setFormData({ ...formData, idPaciente: v?.id_paciente ?? '' });
                            }}
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField select fullWidth label="Prioridade"
                                value={formData.prioridade}
                                onChange={(e) => setFormData({ ...formData, prioridade: e.target.value })}>
                                {Object.entries(PRIORIDADE_CONFIG).map(([k, v]) => (
                                    <MenuItem key={k} value={k}>{v.label}</MenuItem>
                                ))}
                            </TextField>
                            <TextField fullWidth label="Data de Entrada" type="date"
                                value={formData.dataEntrada}
                                onChange={(e) => setFormData({ ...formData, dataEntrada: e.target.value })}
                                InputLabelProps={{ shrink: true }} />
                        </Box>
                        <TextField fullWidth multiline rows={2} label="Observações"
                            value={formData.observacoes}
                            onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
                    <Button onClick={handleSalvar} variant="contained">Adicionar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
